import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Table, TableColumnsType, message, Radio, Spin } from 'antd';
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import FilterArea, { FilterOptions, createDefaultFilterOptions } from './components/FilterArea';
import { normalizeTimezone } from '../../../utils/timezoneUtils';
import {
  GET_DRIVER_LOCATION_TRACKS,
  GetDriverLocationTracksResponse,
  DriverLocationTrackType,
} from './graphql/queries/getDriverLocationTracks';
import { exportGenericToExcel } from '../../../utils/exportUtils';
import { generateDriverLocationTrackMarkerIcon } from '../../../utils/mapMarkerIconUtils';
import { wgsToMapDisplayLatLng } from '../../../utils/coordTransformUtils';

// Google Maps API Key
const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';

// 地图默认配置
const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = {
  lat: 40.7128,
  lng: -74.0060,
};

const defaultZoom = 10;

dayjs.extend(utc);
dayjs.extend(timezone);

const DriverLocationTrackPage: React.FC = () => {
  const { t } = useTranslation();
  // 使用工厂函数确保每次挂载时的默认时间范围都是“当前时间往前7天”
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(() => createDefaultFilterOptions());
  const [tracks, setTracks] = useState<DriverLocationTrackType[]>([]);
  // 地图分批渲染用（避免一次性渲染大量 Marker 卡顿）
  const [renderedTracks, setRenderedTracks] = useState<DriverLocationTrackType[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapInstanceKey, setMapInstanceKey] = useState<number>(0);
  const [contentAreaHeight, setContentAreaHeight] = useState<number>(600);
  // 司机时区：undefined=未选/未探测；null=探测过但无近期数据；string=IANA 时区
  const [driverTimezone, setDriverTimezone] = useState<string | null | undefined>(undefined);
  const mapRef = useRef<google.maps.Map | null>(null);
  const renderJobIdRef = useRef<number>(0);
  const contentAreaRef = useRef<HTMLDivElement | null>(null);
  // 同一司机再次选中命中缓存不重发探测；value 为 null 表示已探测过且无近期数据
  const driverTimezoneCacheRef = useRef<Map<number, string | null>>(new Map());
  // 记录当前正在探测的司机 id，避免响应回来时被中途切换的司机污染
  const probingDriverIdRef = useRef<number | null>(null);

  const centerMapToTrack = useCallback((track: DriverLocationTrackType) => {
    if (!mapRef.current) return;
    const p = wgsToMapDisplayLatLng(track.latitude, track.longitude);
    mapRef.current.panTo(p);
  }, []);

  const toggleSelectTrack = useCallback(
    (track: DriverLocationTrackType, source: 'row' | 'radio' | 'marker') => {
      setSelectedTrackId((prev) => {
        const next = prev === track.id ? null : track.id;
        // 仅在“选中”时移动地图到点的中心；取消选中不移动
        if (next) {
          centerMapToTrack(track);
        }
        return next;
      });
    },
    [centerMapToTrack]
  );

  // 时间显示：根据 timezone 列转换展示
  // - timezone 为 IANA（如 Asia/Shanghai）→ 按该时区显示
  // - timezone 为 UTC±x / GMT±x → 按偏移显示
  // - 无/不合法 → 回退显示 UTC
  const formatTimeByTimezone = useCallback((utcTimeString: string, timezoneStr: string | null | undefined): string => {
    if (!utcTimeString) return '-';
    
    try {
      // locationTimestamp 是 UTC（ISO 8601，通常带 Z）。默认以 UTC 展示，避免被浏览器本地时区自动转换
      const utcTime = dayjs.utc(utcTimeString);
      if (!utcTime.isValid()) {
        return dayjs.utc(utcTimeString).format('YYYY-MM-DD HH:mm:ss');
      }

      if (!timezoneStr) {
        return utcTime.format('YYYY-MM-DD HH:mm:ss');
      }

      const tz = timezoneStr.trim();
      // 处理 UTC±x / GMT±x
      const utcOrGmtMatch = tz.toUpperCase().match(/^(UTC|GMT)([+-]?\d+)$/);
      if (utcOrGmtMatch) {
        const offsetHours = parseInt(utcOrGmtMatch[2], 10);
        const localTime = utcTime.utcOffset(offsetHours);
        return localTime.format('YYYY-MM-DD HH:mm:ss');
      }

      // 处理 IANA 时区（如 Asia/Shanghai）
      try {
        const localTime = utcTime.tz(tz);
        return localTime.format('YYYY-MM-DD HH:mm:ss');
      } catch (e) {
        // 兼容 normalizeTimezone() 可能返回 +08:00 这种格式
        const normalized = normalizeTimezone(tz);
        try {
          const localTime = utcTime.tz(normalized);
          return localTime.format('YYYY-MM-DD HH:mm:ss');
        } catch (e2) {
          return utcTime.format('YYYY-MM-DD HH:mm:ss');
        }
      }
    } catch (error) {
      console.error('Error formatting time:', error);
      return dayjs.utc(utcTimeString).format('YYYY-MM-DD HH:mm:ss');
    }
  }, []);

  // 使用 useJsApiLoader 加载 Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    preventGoogleFontsLoading: true,
  });

  // GraphQL 查询
  const [getDriverLocationTracks, { loading }] = useLazyQuery<GetDriverLocationTracksResponse>(
    GET_DRIVER_LOCATION_TRACKS,
    {
      onCompleted: (data) => {
        setPageLoading(false);
        // 根据 dataSource 过滤（后端已按 locationTimestamp 排序，过滤后仍保持顺序）
        let filteredTracks = data.getDriverLocationTracks;
        if (filterOptions.dataSource !== 'all') {
          filteredTracks = filteredTracks.filter(
            (track) => track.dataSource === filterOptions.dataSource
          );
        }
        // 前端时间排序（固定按时间升序）
        filteredTracks = [...filteredTracks].sort((a, b) => {
          const ta = Date.parse(a.locationTimestamp);
          const tb = Date.parse(b.locationTimestamp);
          const diff = (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
          return diff;
        });
        setTotalCount(filteredTracks.length);
        // 不再在前端分页，直接展示全部过滤后的轨迹
        setTracks(filteredTracks);
        if (filteredTracks.length > 0) {
          // 设置地图边界以显示所有点
          fitMapBounds(filteredTracks);
        }
      },
      onError: (error) => {
        setPageLoading(false);
        message.error(t('mapTool.driverLocationTrack.queryFailed') + ': ' + error.message);
        setTracks([]);
        setRenderedTracks([]);
      },
    }
  );

  // 探测查询：选中司机时用最近 7 天窗口拉一次，取最后一条记录的 timezone 作为筛选参考系
  // 独立的 lazy query，避免与主查询的 onCompleted 相互覆盖 tracks 状态
  const [probeDriverTimezoneQuery] = useLazyQuery<GetDriverLocationTracksResponse>(
    GET_DRIVER_LOCATION_TRACKS,
    { fetchPolicy: 'network-only' }
  );

  // 司机切换：命中缓存直接用；否则发探测请求
  // 用 probingDriverIdRef 做 race guard：快速切换 A→B 时 A 的响应回来会被丢弃，不污染 B 的状态
  const handleDriverChange = useCallback(
    async (driverId: number | undefined) => {
      if (driverId === undefined) {
        probingDriverIdRef.current = null;
        setDriverTimezone(undefined);
        return;
      }
      if (driverTimezoneCacheRef.current.has(driverId)) {
        probingDriverIdRef.current = driverId;
        setDriverTimezone(driverTimezoneCacheRef.current.get(driverId) ?? null);
        return;
      }
      probingDriverIdRef.current = driverId;
      setDriverTimezone(undefined);
      const now = dayjs();
      try {
        const { data } = await probeDriverTimezoneQuery({
          variables: {
            driverAccountId: driverId.toString(),
            startTime: now.subtract(7, 'day').toISOString(),
            endTime: now.toISOString(),
            dataSource: null,
          },
        });
        if (probingDriverIdRef.current !== driverId) return;
        const records = data?.getDriverLocationTracks ?? [];
        // 后端按 locationTimestamp 升序返回，最后一条即最近；无数据则记为 null
        const tz = records.length > 0 ? records[records.length - 1].timezone : null;
        driverTimezoneCacheRef.current.set(driverId, tz);
        setDriverTimezone(tz);
      } catch {
        // 探测失败静默降级为浏览器本地时区（handleFilter 会用 dayjs.tz.guess() 兜底），不打扰用户
        if (probingDriverIdRef.current !== driverId) return;
        setDriverTimezone(null);
      }
    },
    [probeDriverTimezoneQuery]
  );

  // 地图 Marker/Polyline 分批渲染：避免一次性渲染大量点导致卡顿
  useEffect(() => {
    const jobId = ++renderJobIdRef.current;

    if (tracks.length === 0) {
      setRenderedTracks([]);
      return;
    }

    const chunkSize = 100;
    if (tracks.length <= chunkSize) {
      setRenderedTracks(tracks);
      return;
    }

    setRenderedTracks([]);
    let index = 0;

    const pump = () => {
      if (renderJobIdRef.current !== jobId) return;
      const nextIndex = Math.min(index + chunkSize, tracks.length);
      const chunk = tracks.slice(index, nextIndex);
      setRenderedTracks((prev) => prev.concat(chunk));
      index = nextIndex;

      if (index < tracks.length) {
        requestAnimationFrame(pump);
      }
    };

    requestAnimationFrame(pump);

    return () => {
      // 通过 jobId 失效来取消后续批次
      if (renderJobIdRef.current === jobId) {
        renderJobIdRef.current++;
      }
    };
  }, [tracks]);

  const isRenderComplete = renderedTracks.length > 0 && renderedTracks.length === tracks.length;

  // 地图加载回调
  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    mapRef.current = mapInstance;
    setMap(mapInstance);
  }, []);

  // 地图卸载回调
  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
    setMap(null);
  }, []);

  // 设置地图边界以显示所有点
  const fitMapBounds = useCallback((tracks: DriverLocationTrackType[]) => {
    if (!mapRef.current || tracks.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    tracks.forEach((track) => {
      const p = wgsToMapDisplayLatLng(track.latitude, track.longitude);
      bounds.extend(p);
    });

    mapRef.current.fitBounds(bounds);
  }, []);

  // 处理筛选
  const handleFilter = (options: FilterOptions, action: 'search' | 'reset' = 'search') => {
    setFilterOptions(options);

    // reset 场景：只清空数据和地图，不做必填校验和提示
    if (action === 'reset') {
      setPageLoading(false);
      setSelectedTrackId(null);
      setTracks([]);
      setRenderedTracks([]);
      setTotalCount(0);
      setMapInstanceKey((prev) => prev + 1);
      return;
    }

    if (!options.driverAccountId || !options.timeRange[0] || !options.timeRange[1]) {
      setPageLoading(false);
      message.warning(t('mapTool.driverLocationTrack.fillCompleteFilter'));
      return;
    }

    // 验证时间范围不超过7天
    const startTime = options.timeRange[0];
    const endTime = options.timeRange[1];
    const daysDiff = endTime.diff(startTime, 'day');
    if (daysDiff > 7) {
      setPageLoading(false);
      message.warning(t('mapTool.driverLocationTrack.timeRangeExceedsOneWeek') || '时间范围不能超过7天');
      return;
    }

    // 重置选中状态与当前轨迹，避免旧数据在地图上残留
    setSelectedTrackId(null);
    setTracks([]);
    setRenderedTracks([]);
    // 重建 GoogleMap 实例，彻底清理旧的 Polyline/Marker
    setMapInstanceKey((prev) => prev + 1);
    setPageLoading(true);

    // 按司机时区把筛选值解释为 UTC，与表格按 record.timezone 展示形成一致的司机本地视角
    // driverTimezone 为 null（无近期数据）或时区非法时回退到浏览器本地时区
    const fallbackTz = dayjs.tz.guess();
    const effectiveTz = driverTimezone || fallbackTz;
    let startUtcIso: string;
    let endUtcIso: string;
    try {
      startUtcIso = dayjs.tz(startTime.format('YYYY-MM-DD HH:mm:ss'), effectiveTz).utc().toISOString();
      endUtcIso = dayjs.tz(endTime.format('YYYY-MM-DD HH:mm:ss'), effectiveTz).utc().toISOString();
    } catch {
      startUtcIso = startTime.toISOString();
      endUtcIso = endTime.toISOString();
    }

    getDriverLocationTracks({
      variables: {
        driverAccountId: options.driverAccountId.toString(),
        startTime: startUtcIso,
        endTime: endUtcIso,
        // 透传数据来源（后端当前未声明该 GraphQL 变量，多余变量会被忽略）
        dataSource: options.dataSource !== 'all' ? options.dataSource : null,
      },
    });
  };

  // 处理列表行点击（已移除地图响应）
  const handleRowClick = (record: DriverLocationTrackType) => {
    toggleSelectTrack(record, 'row');
  };

  // 处理地图标记点击
  const handleMarkerClick = (trackId: string) => {
    const track = renderedTracks.find((t) => t.id === trackId);
    if (!track) return;
    toggleSelectTrack(track, 'marker');
  };


  // 下载数据
  const handleDownload = () => {
    if (tracks.length === 0) {
      message.warning(t('mapTool.driverLocationTrack.noDataToDownload'));
      return;
    }

    // 按照数据库 driver_location_track 表的列顺序导出
    const headers = [
      t('mapTool.driverLocationTrack.exportHeaders.id'),                // id
      t('mapTool.driverLocationTrack.exportHeaders.driverAccountId'),  // driver_account_id
      t('mapTool.driverLocationTrack.exportHeaders.latitude'),         // latitude
      t('mapTool.driverLocationTrack.exportHeaders.longitude'),        // longitude
      t('mapTool.driverLocationTrack.exportHeaders.horizontalAccuracy'), // horizontal_accuracy
      t('mapTool.driverLocationTrack.exportHeaders.verticalAccuracy'),   // vertical_accuracy
      t('mapTool.driverLocationTrack.exportHeaders.altitude'),           // altitude
      t('mapTool.driverLocationTrack.exportHeaders.locationType'),       // location_type
      t('mapTool.driverLocationTrack.exportHeaders.bearing'),            // bearing
      t('mapTool.driverLocationTrack.exportHeaders.gpsStatus'),          // gps_status
      t('mapTool.driverLocationTrack.exportHeaders.operatingSystem'),    // operating_system
      t('mapTool.driverLocationTrack.exportHeaders.locationTimestamp'),  // location_timestamp
      t('mapTool.driverLocationTrack.exportHeaders.timezone'),           // timezone
      t('mapTool.driverLocationTrack.exportHeaders.phoneModel'),         // phone_model
      t('mapTool.driverLocationTrack.exportHeaders.speed'),              // speed
      t('mapTool.driverLocationTrack.exportHeaders.dataSource'),         // data_source
    ];

    const getRowData = (record: DriverLocationTrackType) => {
      return [
        record.id,                                  // id
        record.driverAccountId,                     // driver_account_id
        record.latitude,                            // latitude
        record.longitude,                           // longitude
        record.horizontalAccuracy ?? '',            // horizontal_accuracy
        record.verticalAccuracy ?? '',              // vertical_accuracy
        record.altitude ?? '',                      // altitude
        record.locationType,                        // location_type
        record.bearing ?? '',                       // bearing
        record.gpsStatus ?? '',                     // gps_status
        record.operatingSystem,                     // operating_system
        formatTimeByTimezone(record.locationTimestamp, record.timezone), // location_timestamp（按 timezone 转换显示）
        record.timezone || '',                      // timezone
        record.phoneModel ?? '',                    // phone_model
        record.speed ?? '',                         // speed
        record.dataSource,                          // data_source：DELIVERY_DRIVER / PICKUP_DRIVER / TMS_DRIVER
      ];
    };

    exportGenericToExcel(
      tracks,
      headers,
      getRowData,
      t('mapTool.driverLocationTrack.exportFileName') || '司机定位轨迹',
      t('mapTool.driverLocationTrack.exportSheetName') || '定位轨迹',
      [
        { wch: 20 },  // 唯一标识 ID
        { wch: 20 },  // 司机唯一标识
        { wch: 15 },  // 定位纬度
        { wch: 15 },  // 定位经度
        { wch: 12 },  // 定位精度（水平）
        { wch: 12 },  // 定位精度（垂直）
        { wch: 12 },  // 海拔高度
        { wch: 15 },  // 定位类型
        { wch: 12 },  // 定位方向角
        { wch: 12 },  // GPS 信号强度
        { wch: 12 },  // 操作系统
        { wch: 20 },  // 定位时间
        { wch: 10 },  // 时区
        { wch: 15 },  // 手机型号
        { wch: 12 },  // 速度
        { wch: 15 },  // 数据来源
      ]
    );

    message.success(t('mapTool.driverLocationTrack.exportSuccess', { count: tracks.length }));
  };

  // 表格列定义：首列使用自定义 Radio 控制选中，可支持“再次点击取消”
  const columns: TableColumnsType<DriverLocationTrackType> = [
    {
      title: '',
      dataIndex: 'selector',
      key: 'selector',
      width: 48,
      render: (_: any, record: DriverLocationTrackType) => (
        <Radio
          checked={record.id === selectedTrackId}
          onClick={(e) => {
            // 避免冒泡到行点击，单独处理选中/取消逻辑
            e.stopPropagation();
            toggleSelectTrack(record, 'radio');
          }}
        />
      ),
    },
    {
      title: t('mapTool.driverLocationTrack.time'),
      dataIndex: 'locationTimestamp',
      key: 'locationTimestamp',
      width: 200,
      render: (text: string, record: DriverLocationTrackType) => {
        return formatTimeByTimezone(text, record.timezone);
      },
    },
    {
      title: t('mapTool.driverLocationTrack.timezone'),
      key: 'timezone',
      width: 100,
      render: (_: any, record: DriverLocationTrackType) => {
        // 从后端返回的 timezone 字段读取并显示
        // 与其他页面保持一致：时区信息由后端计算返回，前端只负责显示
        return record.timezone || 'UTC';
      },
    },
    {
      title: t('mapTool.driverLocationTrack.latitude'),
      dataIndex: 'latitude',
      key: 'latitude',
      width: 100,
      render: (value: number) => value.toFixed(7),
    },
    {
      title: t('mapTool.driverLocationTrack.longitude'),
      dataIndex: 'longitude',
      key: 'longitude',
      width: 100,
      render: (value: number) => value.toFixed(7),
    },
  ];

  // 生成标记图标：必须等 Google Maps 脚本加载完成后才能访问 window.google
  const normalMarkerIcon = useMemo(() => {
    if (!isLoaded || !(window as any).google?.maps) return undefined;
    return generateDriverLocationTrackMarkerIcon('#4285F4', 0, 0);
  }, [isLoaded]);

  const selectedMarkerIcon = useMemo(() => {
    if (!isLoaded || !(window as any).google?.maps) return undefined;
    return generateDriverLocationTrackMarkerIcon('#EA4335', 0, 0);
  }, [isLoaded]);

  // 始终渲染数字标签（按需可再做降载优化）
  const showAllMarkerLabels = true;

  // 准备地图路径点（去除相邻坐标完全相同的点，避免“自己连自己”的伪线段）
  // 线段延后绘制：等点分批渲染完成后再一次性画线，避免“线先画完、点后出现”的视觉效果
  const pathPoints = useMemo(() => {
    if (!isRenderComplete) return [];
    return tracks.map((track) => wgsToMapDisplayLatLng(track.latitude, track.longitude));
  }, [isRenderComplete, tracks]);

  // 当选中轨迹变化时，将对应表格行滚动到可视区域中间，便于查看
  useEffect(() => {
    if (!selectedTrackId) return;
    // antd Table 会在行元素上设置 data-row-key 属性
    const row = document.querySelector<HTMLTableRowElement>(`.ant-table-row[data-row-key="${selectedTrackId}"]`);
    if (row) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [selectedTrackId]);

  // 内容区高度自适应：基于视口高度计算剩余可用空间，避免大小屏出现底部空白
  useEffect(() => {
    const updateContentAreaHeight = () => {
      if (!contentAreaRef.current) return;
      const rect = contentAreaRef.current.getBoundingClientRect();
      // 预留页面底部内边距，确保内容区在可视区域内完整铺满
      const nextHeight = Math.floor(window.innerHeight - rect.top - 24);
      if (nextHeight > 0) {
        setContentAreaHeight(nextHeight);
      }
    };

    updateContentAreaHeight();
    window.addEventListener('resize', updateContentAreaHeight);
    return () => window.removeEventListener('resize', updateContentAreaHeight);
  }, []);

  const tableScrollY = Math.max(contentAreaHeight - 32, 240);

  if (loadError) {
    return <div>{t('mapTool.driverLocationTrack.mapLoadFailed')}</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0 h-full flex flex-col">
      <h4 className="text-xl font-bold mb-4 pt-4">{t('mapTool.driverLocationTrack.title')}</h4>

      {/* 筛选区（包含筛选/重置/下载按钮） */}
      <FilterArea
        filters={filterOptions}
        handleFilter={handleFilter}
        totalCount={totalCount}
        onDownload={handleDownload}
        onDriverChange={handleDriverChange}
      />

      {/* 内容区：列表和地图 */}
      <Spin
        spinning={pageLoading || loading}
        tip={t('mapTool.driverLocationTrack.loading') || 'Loading...'}
        className="flex-1 min-h-0"
        wrapperClassName="h-full"
      >
      <div
        ref={contentAreaRef}
        className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-0"
        style={{ height: `${contentAreaHeight}px` }}
      >
        {/* 结果列表区：与 LocationCorrectionPage 保持一致的 Table 风格（无显式容器样式） */}
        <div className="bg-white rounded-lg shadow-sm flex flex-col min-h-0 h-full">
          <div className="flex-1 min-h-0">
            <Table
              columns={columns}
              dataSource={tracks}
              loading={false}
              rowKey="id"
              sticky
              scroll={{ x: 'max-content', y: tableScrollY }}
              pagination={false}
              size="middle"
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                className: record.id === selectedTrackId ? 'ant-table-row-selected' : '',
              })}
            />
          </div>
          {/* 不再在前端分页，仅保留合计统计 */}
        </div>

        {/* 地图区域：跟随内容区高度自适应铺满 */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden min-h-0 h-full">
          {isLoaded ? (
            <GoogleMap
              key={mapInstanceKey}
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={defaultZoom}
              onLoad={onMapLoad}
              onUnmount={onMapUnmount}
              options={{
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: true,
              }}
            >
                {/* 绘制路径线 */}
                {pathPoints.length > 1 && (
                  <Polyline
                    key={`driver-path-${filterOptions.driverAccountId ?? 'none'}-${pathPoints.length}`}
                    path={pathPoints}
                    options={{
                      strokeColor: '#4285F4',
                      strokeOpacity: 0.6,
                      strokeWeight: 3,
                      clickable: false,
                      zIndex: 1,
                    }}
                  />
                )}

                {/* 绘制标记点 */}
                {renderedTracks.map((track, index) => {
                  const isSelected = selectedTrackId === track.id;
                  const icon = isSelected ? selectedMarkerIcon : normalMarkerIcon;
                  const baseZIndex = 1;
                  const zIndex = isSelected
                    ? baseZIndex + renderedTracks.length + 1
                    : baseZIndex + index;
                  const mapPos = wgsToMapDisplayLatLng(track.latitude, track.longitude);
                  return (
                    <Marker
                      key={`${track.id}-${renderedTracks.length}-${isSelected ? 'sel' : 'unsel'}-${index}`}
                      position={mapPos}
                      icon={icon}
                      label={{
                        text: String(index + 1),
                        color: '#FFFFFF',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                      onClick={() => handleMarkerClick(track.id)}
                      zIndex={zIndex}
                      title={`标记点 ${index + 1}`}
                      visible={true}
                    />
                  );
                })}
            </GoogleMap>
          ) : (
            <div className="w-full h-full flex items-center justify-center">{t('mapTool.driverLocationTrack.mapLoading')}</div>
          )}
        </div>
      </div>
      </Spin>
    </div>
  );
};

export default DriverLocationTrackPage;

